import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LeadForm } from '../components/forms/LeadForm';
import { Camera, BarChart3, Megaphone, Handshake, Building2, DollarSign, ArrowRight, Phone, Calendar } from 'lucide-react';

const sellingSteps = [
  { icon: BarChart3, title: 'Market Analysis', description: 'Get a comprehensive market analysis to price your property competitively and attract serious buyers.' },
  { icon: Camera, title: 'Professional Marketing', description: 'High-quality photos, virtual tours, and targeted marketing to showcase your property.' },
  { icon: Megaphone, title: 'Maximum Exposure', description: 'List on MLS, social media, and our network of buyers for maximum visibility.' },
  { icon: Handshake, title: 'Negotiate & Close', description: 'Expert negotiation to get you the best price and smooth closing coordination.' }
];

const benefits = [
  'Free home valuation', 'Professional photography included', 'Virtual tour creation',
  'Social media marketing', 'MLS listing exposure', 'Open house coordination',
  'Expert price negotiation', 'Closing assistance'
];

export const Sellers = () => {
  return (
    <div data-testid="sellers-page">
      {/* Home Valuation Funnel - TOP OF PAGE */}
      <section className="py-12 bg-[#0a0a0a]" data-testid="seller-valuation-funnel">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="lg:pt-6">
              <div className="inline-block px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
                Free Home Valuation
              </div>
              <h1 className="text-4xl sm:text-5xl font-light text-[#F5F5F0] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Find Out What Your Home Is <span className="text-gold-gradient">Really Worth</span>
              </h1>
              <p className="text-lg text-[#808080] mb-6">
                Get a no-obligation market analysis from our team. Know your home's true value before making any decisions.
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                <a href="tel:7863560878">
                  <Button className="btn-gold" data-testid="seller-call-btn">
                    <Phone className="w-4 h-4 mr-2" /> Call (786) 356-0878
                  </Button>
                </a>
                <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="btn-outline-gold" data-testid="seller-book-btn">
                    <Calendar className="w-4 h-4 mr-2" /> Book a Call
                  </Button>
                </a>
              </div>
              <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-lg p-4">
                <p className="text-[#F5F5F0] text-sm font-medium mb-2">Why get a valuation?</p>
                <ul className="space-y-1.5 text-[#808080] text-xs">
                  <li className="flex items-center"><div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mr-2" />Know your equity position</li>
                  <li className="flex items-center"><div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mr-2" />Price competitively from day one</li>
                  <li className="flex items-center"><div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mr-2" />Understand current market conditions</li>
                  <li className="flex items-center"><div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mr-2" />No obligation — 100% free</li>
                </ul>
              </div>
            </div>
            <LeadForm 
              source="seller"
              title="Get Your Free Home Valuation"
              subtitle="Tell us about your property and we'll provide a market analysis within 24 hours."
              buttonText="Get My Home Value"
              showPropertyInterest={true}
            />
          </div>
        </div>
      </section>

      {/* Selling Process */}
      <section className="py-20 bg-[#111111]" data-testid="selling-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">Our Process</p>
            <h2 className="section-title mt-2">How We Sell Your Home</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sellingSteps.map((step, index) => (
              <div key={index} className="card-hover bg-[#0a0a0a] p-6 rounded-lg text-center">
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <span className="text-[#D4AF37] text-2xl font-light" style={{ fontFamily: 'Playfair Display' }}>0{index + 1}</span>
                <h3 className="text-xl font-light text-[#F5F5F0] mt-2 mb-3" style={{ fontFamily: 'Playfair Display' }}>{step.title}</h3>
                <p className="text-[#808080] text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits + CTA */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="sellers-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-overline">Why Choose SRI</p>
              <h2 className="section-title mt-2 mb-8">What's Included When You List With Us</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center text-[#F5F5F0]">
                    <div className="w-2 h-2 rounded-full bg-[#D4AF37] mr-3 flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-xl p-8">
              <h3 className="text-2xl font-light text-[#F5F5F0] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                Ready to Sell?
              </h3>
              <p className="text-[#808080] mb-6">
                Schedule a free consultation to discuss your selling goals and get a personalized marketing plan.
              </p>
              <div className="space-y-3">
                <a href="tel:7863560878" className="block">
                  <Button className="btn-gold w-full" data-testid="sellers-cta-call">
                    <Phone className="w-4 h-4 mr-2" /> Call (786) 356-0878
                  </Button>
                </a>
                <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="btn-outline-gold w-full" data-testid="sellers-calendly-btn">
                    <Calendar className="w-4 h-4 mr-2" /> Book Free Consultation
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-[#111111]" data-testid="sellers-related">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="section-overline">One Call Handles It All</p>
            <h2 className="section-title mt-2">More Ways We Can Help</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card-hover bg-[#0a0a0a] p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <DollarSign className="w-8 h-8 text-[#D4AF37] mr-4" />
                <div>
                  <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>Simple Mortgage Financing (SMF)</h3>
                  <p className="text-[#D4AF37] text-xs">Help your buyers qualify</p>
                </div>
              </div>
              <p className="text-[#808080] mb-4 text-sm">FHA, VA, Conventional & DSCR loans — we help your buyers get pre-approved fast.</p>
              <Link to="/financing"><Button variant="outline" className="btn-outline-gold">Explore Financing <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            </div>
            <div className="card-hover bg-[#0a0a0a] p-8 rounded-lg">
              <div className="flex items-center mb-4">
                <Building2 className="w-8 h-8 text-[#D4AF37] mr-4" />
                <div>
                  <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>Property Management (SRIM)</h3>
                  <p className="text-[#D4AF37] text-xs">Not ready to sell? Rent instead.</p>
                </div>
              </div>
              <p className="text-[#808080] mb-4 text-sm">Full-service property management — tenant placement, rent collection, maintenance.</p>
              <Link to="/property-management"><Button variant="outline" className="btn-outline-gold">Learn More <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
